/**
 * WhosOnline Component - Compact Avatar Stack
 * Who's Online Feature - Option A Implementation
 * 
 * Features:
 * - Overlapping avatar stack
 * - Real-time status indicators (green/yellow/gray)
 * - Expandable dropdown with detailed user info
 * - Current page tracking
 * - Role badges
 * 
 * Design inspired by:
 * - Figma: Collaborative presence avatars
 * - Linear: Clean, minimal presence UI
 * - Notion: Team activity indicators
 * 
 * @version 1.0.0
 * @date February 2026
 */
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Circle, Clock, MapPin, Users } from 'lucide-react';
import { usePresence } from '../contexts/PresenceContext';

/**
 * Get status color class
 */
const getStatusColor = (status) => {
  switch (status) {
    case 'active': return 'bg-emerald-500';
    case 'idle': return 'bg-amber-500';
    case 'away': return 'bg-slate-500';
    default: return 'bg-slate-500';
  }
};

/**
 * Get role badge color
 */
const getRoleBadgeColor = (role) => {
  const colors = {
    admin: 'bg-red-500/20 text-red-300',
    designs_manager: 'bg-purple-500/20 text-purple-300',
    designs_engineer: 'bg-blue-500/20 text-blue-300',
    quality_manager: 'bg-emerald-500/20 text-emerald-300',
    quality_engineer: 'bg-teal-500/20 text-teal-300',
    inventory_manager: 'bg-amber-500/20 text-amber-300',
    operator: 'bg-orange-500/20 text-orange-300',
    viewer: 'bg-slate-500/20 text-slate-300',
  };
  return colors[role] || colors.viewer;
};

/**
 * Get user initials
 */
const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Format role for display
 */
const formatRole = (role) => {
  if (!role) return 'Viewer';
  return role.replace(/_/g, ' ');
};

/**
 * Avatar component with status indicator
 */
const UserAvatar = ({ user, size = 'md', showStatus = true, className = '' }) => {
  const sizeClasses = {
    sm: 'w-7 h-7 text-xs',
    md: 'w-8 h-8 text-xs',
    lg: 'w-10 h-10 text-sm',
  };
  
  const statusSizeClasses = {
    sm: 'w-2.5 h-2.5',
    md: 'w-3 h-3',
    lg: 'w-3.5 h-3.5',
  };
  
  return (
    <div className={`relative ${className}`}>
      <div 
        className={`${sizeClasses[size]} rounded-full bg-[#C8B59A] border-2 border-[#101957] flex items-center justify-center font-bold text-[#101957]`}
        title={user.display_name || user.full_name}
      >
        {getInitials(user.display_name || user.full_name)}
      </div>
      {showStatus && (
        <span 
          className={`absolute -bottom-0.5 -right-0.5 ${statusSizeClasses[size]} rounded-full border-2 border-[#101957] ${getStatusColor(user.status)}`}
          title={user.status === 'active' ? 'Online' : user.status === 'idle' ? 'Idle' : 'Away'}
        />
      )}
    </div>
  );
};

/**
 * User row in dropdown
 */
const UserRow = ({ user, onClick }) => {
  return (
    <button
      onClick={() => onClick?.(user)}
      className="w-full px-4 py-3 hover:bg-white/5 transition-colors border-b border-slate-700/50 last:border-0 text-left"
      data-testid={`online-user-${user.user_id}`}
    >
      <div className="flex items-start space-x-3">
        <UserAvatar user={user} size="lg" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-white truncate">
              {user.display_name || user.full_name}
            </p>
            <span className={`text-xs px-1.5 py-0.5 rounded capitalize ${getRoleBadgeColor(user.role)}`}>
              {formatRole(user.role)}
            </span>
          </div>
          <div className="mt-1 flex items-center space-x-3 text-xs text-slate-400">
            <span className="flex items-center space-x-1">
              <Clock className="w-3 h-3" />
              <span>{user.last_activity_human || 'Just now'}</span>
            </span>
            <span className="flex items-center space-x-1">
              <MapPin className="w-3 h-3" />
              <span className="truncate">{user.current_page || 'Dashboard'}</span>
            </span>
          </div>
        </div>
      </div>
    </button>
  );
};

/**
 * Main WhosOnline component
 */
const WhosOnline = ({ onUserClick }) => {
  const { isConnected, onlineUsers, activeCount, idleCount } = usePresence();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Get display users (show first 3)
  const displayUsers = onlineUsers.slice(0, 3);
  const remainingCount = Math.max(0, onlineUsers.length - 3);
  
  // Handle user click
  const handleUserClick = (user) => {
    setIsOpen(false);
    onUserClick?.(user);
  };
  
  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors"
        data-testid="whos-online-trigger"
      >
        {/* Avatar Stack */}
        <div className="flex -space-x-2">
          {displayUsers.length > 0 ? (
            <>
              {displayUsers.map((user, idx) => (
                <UserAvatar
                  key={user.user_id}
                  user={user}
                  size="md"
                  className={`transform hover:scale-110 hover:z-10 transition-transform`}
                  style={{ zIndex: 3 - idx }}
                />
              ))}
              {remainingCount > 0 && (
                <div className="w-8 h-8 rounded-full bg-slate-600 border-2 border-[#101957] flex items-center justify-center text-xs font-medium text-white">
                  +{remainingCount}
                </div>
              )}
            </>
          ) : (
            <div className="w-8 h-8 rounded-full bg-slate-700 border-2 border-[#101957] flex items-center justify-center">
              <Users className="w-4 h-4 text-slate-400" />
            </div>
          )}
        </div>
        
        {/* Online indicator */}
        <div className="flex items-center space-x-1.5 text-slate-300">
          <Circle 
            className={`w-2.5 h-2.5 ${isConnected ? 'fill-emerald-500 text-emerald-500' : 'fill-slate-500 text-slate-500'}`} 
          />
          <span className="text-sm font-medium">
            {activeCount} Online
          </span>
        </div>
        
        <ChevronDown 
          className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      
      {/* Dropdown */}
      {isOpen && (
        <div 
          className="absolute right-0 top-full mt-2 w-80 bg-[#0d1442] border border-slate-700 rounded-xl shadow-2xl overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200"
          data-testid="whos-online-dropdown"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between bg-slate-800/30">
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-semibold text-white">Team Activity</span>
            </div>
            <div className="flex items-center space-x-3 text-xs">
              <span className="flex items-center space-x-1 text-emerald-400">
                <Circle className="w-2 h-2 fill-current" />
                <span>{activeCount}</span>
              </span>
              {idleCount > 0 && (
                <span className="flex items-center space-x-1 text-amber-400">
                  <Circle className="w-2 h-2 fill-current" />
                  <span>{idleCount}</span>
                </span>
              )}
            </div>
          </div>
          
          {/* User List */}
          <div className="max-h-80 overflow-y-auto">
            {onlineUsers.length > 0 ? (
              onlineUsers.map((user) => (
                <UserRow 
                  key={user.user_id} 
                  user={user} 
                  onClick={handleUserClick}
                />
              ))
            ) : (
              <div className="px-4 py-8 text-center">
                <Users className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                <p className="text-sm text-slate-400">
                  {isConnected ? 'No other users online' : 'Connecting...'}
                </p>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="px-4 py-2 border-t border-slate-700 bg-slate-800/20">
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span className="flex items-center space-x-1">
                <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-slate-500'}`} />
                <span>{isConnected ? 'Live' : 'Offline'}</span>
              </span>
              <span>Real-time updates</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhosOnline;
